import { IconCheckFill } from "@/assets/icons/geist/IconCheckFill";
import { IconWarning } from "@/assets/icons/geist/IconWarning";
import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import { Card } from "@/components/podkit/Card";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading1, Heading2 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { SourceControlProviderIcon } from "@/components/runners/details/SourceControlProviderIcon";
import { type SCMAuthenticationStage, useSCMAuthentication } from "@/hooks/use-scm-authentication.ts";
import type { TrackLocation } from "@/hooks/use-segment";
import { useCreateAndVerifyHostAuthenticationTokenPAT } from "@/queries/runner-configuration-queries";
import { useRunner, type PlainAuthenticationMethodPersonalAccessToken } from "@/queries/runner-queries";
import { formatError } from "@/utils/errors";
import { Code } from "@connectrpc/connect";
import { type FC, type PropsWithChildren, useCallback, useId, useMemo, useState } from "react";

export type SCMAuthenticationProps = {
    stage: SCMAuthenticationStage;
    verificationError?: Error;
    runnerId: string;
    repoURL: string;
    // If the authentication URL isn't provided it means the runner doesn't support OAuth
    authenticationUrl?: string;
    patAuth?: PlainAuthenticationMethodPersonalAccessToken;
    scmId: string;
    scmName: string;
    onClose: () => void;
    onClickContinue: () => void;
    handleConnectWithOAuth: () => void;
    handleConnectWithPAT: () => void;
    reset: () => void;
    handleRemoveConnection: () => Promise<unknown>;
};

type AuthenticationMode = "oauth" | "pat";

export const SCMAuthentication: FC<SCMAuthenticationProps> = ({
    authenticationUrl,
    scmId,
    patAuth,
    handleConnectWithOAuth,
    handleConnectWithPAT,
    handleRemoveConnection,
    onClickContinue,
    onClose,
    reset,
    stage,
    repoURL,
    verificationError,
    runnerId,
    scmName,
}) => {
    const [mode, setMode] = useState<AuthenticationMode>(authenticationUrl ? "oauth" : "pat");

    if (stage == "out_of_sync") {
        return <SCMAuthenticationOutofSync scmName={scmName} />;
    }

    if (stage == "required" && mode == "oauth") {
        return (
            <SCMAuthenticationRequiredOAuth
                patSupported={!!patAuth}
                runnerId={runnerId}
                scmId={scmId}
                scmName={scmName}
                onClick={handleConnectWithOAuth}
                onClickUsePAT={() => {
                    reset();
                    setMode("pat");
                }}
                data-tracking-id-none // handled in component
            />
        );
    }

    if (stage == "required" && mode == "pat" && patAuth) {
        return (
            <SCMAuthenticationRequiredPAT
                patAuth={patAuth}
                oauthSupported={!!authenticationUrl}
                repoURL={repoURL}
                runnerId={runnerId}
                scmId={scmId}
                scmName={scmName}
                onSuccess={handleConnectWithPAT}
                onClickUseOAuth={() => {
                    reset();
                    setMode("oauth");
                }}
            />
        );
    }

    if (stage == "pending") {
        return <SCMAuthenticationPending authenticationUrl={authenticationUrl} scmId={scmId} scmName={scmName} />;
    }

    if (stage == "verifying") {
        return <SCMAuthenticationVerifying scmId={scmId} />;
    }

    if (stage == "error") {
        return (
            <SCMAuthenticationError
                error={verificationError}
                handleRemoveConnection={handleRemoveConnection}
                onClose={onClose}
            />
        );
    }

    if (stage == "successful") {
        return <SCMAuthenticationSuccessful handleConnectionVerified={onClickContinue} scmName={scmName} />;
    }

    return <LoadingState />;
};

export type SCMAuthenticationModalProps = Omit<
    SCMAuthenticationProps,
    "stage" | "reset" | "handleConnectWithOAuth" | "handleConnectWithPAT" | "handleRemoveConnection"
> & {
    "data-track-location": TrackLocation;
};
export const SCMAuthenticationModal: FC<SCMAuthenticationModalProps> = (p) => {
    const props = useSCMAuthentication({
        runnerId: p.runnerId,
        repoURL: p.repoURL,
        authenticationUrl: p.authenticationUrl,
    });
    const combined: SCMAuthenticationProps = { ...p, ...props };
    return (
        <Dialog open onOpenChange={p.onClose}>
            <DialogContent
                className="min-h-0 max-w-lg self-center bg-surface-glass p-8"
                data-track-location={p["data-track-location"]}
            >
                <DialogTitle className="sr-only">{`Authentication for ${p.scmName}`}</DialogTitle>
                <DialogDescription className="sr-only">
                    {`Configure authentication for ${p.scmName} repository access`}
                </DialogDescription>
                <div className="flex flex-col items-center gap-4 px-4 pt-4">
                    <SCMAuthentication {...combined} />
                </div>
            </DialogContent>
        </Dialog>
    );
};

type SCMAuthenticationRequiredPATProps = {
    runnerId: string;
    repoURL: string;
    oauthSupported: boolean;
    scmId: string;
    scmName: string;
    onSuccess: (hostAuthenticationTokenId: string) => void;
    onClickUseOAuth: () => void;
    patAuth: PlainAuthenticationMethodPersonalAccessToken;
};

const SCMAuthenticationRequiredPAT: FC<SCMAuthenticationRequiredPATProps> = ({
    runnerId,
    repoURL,
    oauthSupported,
    scmId,
    scmName,
    onSuccess,
    onClickUseOAuth,
    patAuth,
}) => {
    const { toast } = useToast();
    const scmHost = useMemo(() => {
        try {
            return new URL(repoURL).host;
        } catch {
            // ignore invalid URLs
        }
    }, [repoURL]);
    const [pat, setPAT] = useState("");
    const [repoUrlToAuthenticate, setRepoUrlToAuthenticate] = useState("");
    const createAndVerifyToken = useCreateAndVerifyHostAuthenticationTokenPAT();

    // If the repoURL only contains a host, we don't need to parse the context URL.
    const noParseableContextUrlProvided = useMemo(() => {
        try {
            return new URL(repoURL).pathname === "/";
        } catch {
            // ignore invalid URLs
        }
        return true; // not parseable
    }, [repoURL]);

    const handleConnect = useCallback(async () => {
        if (!scmHost) {
            return;
        }
        try {
            const token = await createAndVerifyToken.mutateAsync({
                pat: pat,
                runnerId: runnerId,
                scmHost: scmHost,
                repoUrl: noParseableContextUrlProvided ? repoUrlToAuthenticate : repoURL,
            });
            if (token) {
                onSuccess(token.id);
            }
        } catch (error) {
            toast({
                title: "We couldn't create a token. Sorry about that! Please retry.",
                description: formatError(error),
            });
        }
    }, [
        runnerId,
        createAndVerifyToken,
        onSuccess,
        pat,
        repoUrlToAuthenticate,
        repoURL,
        scmHost,
        noParseableContextUrlProvided,
        toast,
    ]);

    const loading = createAndVerifyToken.isPending;
    const disabled = loading || !pat || (noParseableContextUrlProvided && !repoUrlToAuthenticate);

    const id = useId();

    return (
        <>
            <SCMAuthenticationRequiredHeading runnerId={runnerId} scmId={scmId} scmName={scmName} />

            <NumberedCard number={1} heading="Create a Personal Access Token">
                <Text className="max-w-xs text-base">
                    Enable read/write access to repository content.&nbsp;
                    <a href={patAuth.docsUrl} target="_blank" rel="noreferrer" className="text-content-link">
                        Learn more
                    </a>
                    .
                </Text>
                {patAuth.createUrl && (
                    <Text className="pt-5 text-base font-bold">
                        Go to {scmHost} &gt; Settings &gt;{" "}
                        <a href={patAuth.createUrl} target="_blank" rel="noreferrer" className="text-content-link">
                            Developer Settings
                        </a>
                    </Text>
                )}
            </NumberedCard>

            <NumberedCard number={2} heading="Paste your Personal Access Token">
                <Text className="max-w-xs text-base">Tokens are encrypted. You can manage them in settings later.</Text>
                <div className="flex flex-col gap-2 pt-5">
                    <Label htmlFor={id}>Paste your Personal Access Token</Label>
                    <Input
                        id={id}
                        placeholder={patAuth.example}
                        value={pat}
                        onChange={(e) => setPAT(e.target.value)}
                        data-testid="personal-access-token-input"
                        type="password"
                        name="personal-access-token"
                    />
                </div>
            </NumberedCard>

            {noParseableContextUrlProvided && (
                <NumberedCard number={3} heading="Paste your repository URL">
                    <Text className="max-w-xs text-base">
                        We&apos;ll verify your PAT has access to this repository.
                    </Text>
                    <div className="flex flex-col gap-2 pt-5">
                        <Input
                            placeholder={`https://${scmHost}/gitpod-io/empty`}
                            value={repoUrlToAuthenticate}
                            onChange={(e) => setRepoUrlToAuthenticate(e.target.value)}
                            data-testid="repo-url-to-authenticate-input"
                            type="text"
                        />
                    </div>
                </NumberedCard>
            )}

            <ErrorMessage
                error={createAndVerifyToken.error}
                className="text-center"
                messages={{
                    [Code.FailedPrecondition]:
                        "Could not find or access the repository. Please verify the repository name and access permissions of the PAT",
                    [Code.NotFound]:
                        "Could not find or access the repository. Please verify the repository name and access permissions of the PAT",
                }}
            />
            <Button
                loading={createAndVerifyToken.isPending}
                disabled={disabled}
                onClick={handleConnect}
                variant="primary"
                className="w-full max-w-sm font-bold"
                data-testid="save-personal-access-token"
                data-tracking-id="authenticate-scm-with-pat-scm-authentication-required-pat"
            >
                Connect
            </Button>
            {oauthSupported && (
                <Button
                    variant={"link"}
                    onClick={onClickUseOAuth}
                    className="h-5 p-0 text-base font-normal hover:text-content-link"
                    data-tracking-id="authenticate-scm-with-oauth-scm-authentication-required-pat"
                >
                    <span>Or use {scmName} OAuth</span>
                </Button>
            )}
        </>
    );
};

type SCMAuthenticationRequiredOAuthProps = {
    runnerId: string;
    patSupported: boolean;
    scmId: string;
    scmName: string;
    onClick: () => void;
    onClickUsePAT: () => void;
};

const SCMAuthenticationOutofSync: FC<{ scmName: string }> = ({ scmName }) => {
    return (
        <>
            <Heading1>Out of sync</Heading1>
            <Text>
                {scmName} is already configured for this runner. If you recently removed it, the runner may take up to
                10 minutes to update. Please try again later.
            </Text>
        </>
    );
};

const SCMAuthenticationRequiredOAuth: FC<SCMAuthenticationRequiredOAuthProps> = ({
    onClick,
    onClickUsePAT,
    runnerId,
    patSupported,
    scmId,
    scmName,
}) => {
    return (
        <>
            <SCMAuthenticationRequiredHeading runnerId={runnerId} scmId={scmId} scmName={scmName} />
            <Button
                onClick={() => onClick()}
                variant={"primary"}
                className="w-full font-bold"
                data-testid="connect-with-github"
                data-tracking-id="authenticate-scm-with-oauth-scm-authentication-required-oauth"
            >
                Connect
            </Button>
            {patSupported && (
                <Button
                    variant={"link"}
                    onClick={() => onClickUsePAT()}
                    className="h-5 border-0 p-0 text-base font-normal hover:text-content-link"
                    data-testid="provide-personal-access-token"
                    data-tracking-id="authenticate-scm-with-pat-scm-authentication-required-oauth"
                >
                    Or provide a Personal Access Token
                </Button>
            )}
        </>
    );
};

type SCMAuthenticationPendingProps = {
    authenticationUrl?: string;
    scmId: string;
    scmName: string;
};

const SCMAuthenticationPending: FC<SCMAuthenticationPendingProps> = ({ authenticationUrl, scmId, scmName }) => {
    return (
        <>
            <div className="flex flex-col items-center">
                <Heading1>Waiting to connect</Heading1>
                <Text>Please complete the authentication at {scmName}</Text>
            </div>
            <SourceControlProviderIcon scmId={scmId} className="my-2 size-[50px]" />
            <Text className="text-base">
                If the window didn&#x2019;t open,{" "}
                <a href={authenticationUrl} target="_blank" rel="noreferrer" className="text-content-link">
                    you can reopen it here
                </a>
                .
            </Text>
        </>
    );
};

const SCMAuthenticationVerifying: FC<{ scmId: string }> = ({ scmId }) => {
    return (
        <>
            <div className="flex flex-col items-center">
                <Heading1>Verifying connection</Heading1>
                <Text>Checking accesses to the repository</Text>
            </div>
            <SourceControlProviderIcon scmId={scmId} size="lg" className="my-2 size-[50px]" />
        </>
    );
};

type ErrorProps = {
    error?: Error;
    handleRemoveConnection: () => Promise<unknown>;
    onClose: () => void;
};

const SCMAuthenticationError: FC<ErrorProps> = ({ error, handleRemoveConnection, onClose }) => {
    const handleClose = useCallback(async () => {
        try {
            await handleRemoveConnection();
        } catch (error) {
            console.error("Failed to remove connection", error);
        }
        onClose();
    }, [handleRemoveConnection, onClose]);
    return (
        <>
            <IconWarning size="lg" className="size-8 text-content-red" />

            <div className="flex flex-col items-center gap-2">
                <Heading1>Repository access failed</Heading1>
                <Text className="p-4 font-mono text-sm">{formatError(error)}</Text>
            </div>

            <Button
                variant="primary"
                onClick={handleClose}
                className="w-full"
                data-tracking-id="close-scm-error-scm-authentication"
            >
                Close
            </Button>
        </>
    );
};

type ConnectionCompletedProps = {
    handleConnectionVerified: () => void;
    scmName: string;
};

const SCMAuthenticationSuccessful: FC<ConnectionCompletedProps> = ({ handleConnectionVerified, scmName }) => {
    return (
        <>
            <div className="flex flex-col items-center">
                <Heading1>{scmName} successfully connected</Heading1>
                <Text>Continue to create the environment</Text>
            </div>

            <IconCheckFill size="lg" className="size-14 text-content-green" />

            <Button
                variant="primary"
                onClick={handleConnectionVerified}
                data-testid="continue"
                className="w-full max-w-sm"
                data-tracking-id="confirm-success-scm-authentication"
            >
                Continue
            </Button>
        </>
    );
};

type SCMAuthenticationRequiredHeadingProps = {
    runnerId: string;
    scmId?: string;
    scmName: string;
};

const SCMAuthenticationRequiredHeading: FC<SCMAuthenticationRequiredHeadingProps> = ({ runnerId, scmId, scmName }) => {
    const { data: runner } = useRunner(runnerId);
    return (
        <>
            <div className="flex flex-col items-center gap-2 pt-4">
                <Heading1>Connect {scmName}</Heading1>
                <Text className="text-center text-lg text-content-secondary">
                    All authentication happens on your infrastructure, ensuring maximum security and control.
                </Text>
            </div>
            <div className="flex flex-col items-center gap-2">
                {runner && (
                    <>
                        <SourceControlProviderIcon scmId={scmId} className="size-[50px]" />
                        <div className="flex flex-row items-center justify-between">
                            <div className="flex flex-col items-center">
                                <div className="px-4 py-1">
                                    Runner{" "}
                                    <span className="font-bold text-content-primary group-data-[disabled]:text-content-tertiary">
                                        {runner.name || "Unknown runner"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

const NumberedCard: FC<PropsWithChildren & { number: number; heading: string }> = ({ number, heading, children }) => {
    return (
        <Card className="flex flex-row justify-items-start gap-3 bg-surface-secondary">
            <div>{number}</div>
            <div className="flex flex-col gap-1">
                <Heading2>{heading}</Heading2>
                {children}
            </div>
        </Card>
    );
};
